import type { LucideIcon } from 'lucide-react';
import { useId, useRef, useState, type ReactNode } from 'react';
import { Button } from '../../../../components/Button';
import { Input } from '../../../../components/Input';
import { Popover, usePopover } from '../../../../components/Popover';
import { useFocusOnOpen } from '../../../../lib/use-focus-on-open';
import styles from './ConfirmTyped.module.css';

/**
 * A button for something that can't be undone, which asks first in a small pop-up beside itself (never centred) and
 * goes only once the confirmation word is typed (06-qol §4: "For irreversible operations, require typing a
 * confirmation word"). The word box takes the focus as the pop-up opens (review W4), and Enter confirms once it matches.
 */
export function ConfirmTyped({
  label,
  icon,
  variant = 'outline',
  disabled = false,
  isPending = false,
  title,
  body,
  word,
  confirmLabel,
  onConfirm,
}: {
  readonly label: string;
  readonly icon?: LucideIcon;
  readonly variant?: 'outline' | 'solid' | 'danger' | 'dangerSolid';
  readonly disabled?: boolean;
  readonly isPending?: boolean;
  readonly title: string;
  /** Exactly what will happen. */
  readonly body: ReactNode;
  readonly word: string;
  readonly confirmLabel: string;
  readonly onConfirm: () => void;
}): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const [typed, setTyped] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const inputId = useId();
  const hintId = useId();
  useFocusOnOpen(inputRef, isOpen);
  const isMatched = typed.trim().toLowerCase() === word.toLowerCase();

  const shut = (): void => {
    setTyped('');
    close();
  };
  const confirm = (): void => {
    if (!isMatched) return;
    shut();
    onConfirm();
  };

  return (
    <>
      <Button ref={triggerRef} variant={variant} size="sm" icon={icon} disabled={disabled} isPending={isPending} aria-expanded={isOpen} onClick={toggle}>
        {label}
      </Button>
      <Popover anchor={anchor} onClose={shut} align="right" width={340} label={title}>
        <form
          className={styles.confirm}
          onSubmit={(event) => {
            event.preventDefault();
            confirm();
          }}
        >
          <h3 className={styles.title}>{title}</h3>
          <div className={styles.body}>{body}</div>
          <label className={styles.label} htmlFor={inputId}>
            Type <strong>{word}</strong> to confirm
          </label>
          <Input ref={inputRef} id={inputId} aria-describedby={hintId} value={typed} autoComplete="off" spellCheck={false} onChange={(event) => setTyped(event.target.value)} />
          <span id={hintId} className={styles.hint}>
            This can’t be undone.
          </span>
          <div className={styles.actions}>
            <Button type="button" variant="ghost" size="sm" onClick={shut}>
              Cancel
            </Button>
            <Button type="submit" variant="dangerSolid" size="sm" disabled={!isMatched}>
              {confirmLabel}
            </Button>
          </div>
        </form>
      </Popover>
    </>
  );
}
